import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import SearchIcon from "@material-ui/icons/Search";
import type { StatusFilter } from "./utils";

interface Props {
  status: StatusFilter;
  counts: Record<StatusFilter, number>;
  search: string;
  onStatusChange: (status: StatusFilter) => void;
  onSearchChange: (search: string) => void;
  onSearchSubmit: () => void;
}

const statuses: StatusFilter[] = ["all", "blocked", "released"];

export default function Toolbar({
  status,
  counts,
  search,
  onStatusChange,
  onSearchChange,
  onSearchSubmit,
}: Props) {
  const { t } = useTranslation("mxheroShares");
  const classes = useStyles();

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      gridGap={16}
      marginBottom={2}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={status}
        className={classes.tabs}
        onChange={(_, value: StatusFilter | null) => {
          if (value) onStatusChange(value);
        }}
      >
        {statuses.map(option => (
          <ToggleButton key={option} value={option} className={classes.tab}>
            {t(`malware.filters.${option}`)}
            <span className={classes.count}>{counts[option]}</span>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <form
        className={classes.searchForm}
        onSubmit={event => {
          event.preventDefault();
          onSearchSubmit();
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          className={classes.search}
          placeholder={t("malware.list.searchPlaceholder")}
          value={search}
          onChange={event => onSearchChange(event.target.value)}
          inputProps={{ "data-testid": "malware-search", maxLength: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          color="primary"
          variant="contained"
          disableElevation
          data-testid="malware-search-btn"
        >
          {t("common:search")}
        </Button>
      </form>
    </Box>
  );
}

const useStyles = makeStyles(theme => ({
  tabs: { backgroundColor: theme.palette.background.paper, padding: 4 },
  tab: {
    border: 0,
    borderRadius: 4,
    padding: "6px 14px",
    textTransform: "none",
    "&.Mui-selected": { backgroundColor: "#e3f4fb", color: "#04688a" },
  },
  count: { marginLeft: 8, fontSize: "0.75rem", opacity: 0.75 },
  searchForm: { display: "flex", alignItems: "center", gap: 8 },
  search: { width: 320, maxWidth: "100%", backgroundColor: "white" },
}));
